import { Alert, Button, Input, Modal } from 'antd';
import { Profile } from 'oidc-client';
import React, { useState } from 'react';
import { isUserAdmin } from '../../../helpers/CheckPermissionHelper';
import { apiErrorText } from '../../../helpers/NotificationHelper';
import authService from '../../../components/api-authorization/AuthorizeService';
import { MatchesClient } from '../../../utils/api';
import BulkCreateValidatorGames from './BulkCreateValidatorGames';

interface Props {
    matchId: number;
    userProfile?: Profile;
}

export default function CreateValidatorGame({ matchId, userProfile }: Props): React.ReactElement | null {
    const [visible, setVisible] = useState(false);
    const [email, setEmail] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>();
    const [createdId, setCreatedId] = useState<number>();
    if (!authService.validationGamesEnabled || !userProfile || !isUserAdmin(userProfile)) return null;

    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    const create = async (): Promise<void> => {
        if (saving || !validEmail) return;
        setSaving(true);
        setError(undefined);
        try {
            setCreatedId(await new MatchesClient().createValidatorGame(matchId, { email: email.trim() }));
        } catch (failure) {
            setError(apiErrorText(failure));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Button onClick={(): void => {
                setEmail('');
                setError(undefined);
                setCreatedId(undefined);
                setVisible(true);
            }}>Create validator game</Button>
            <BulkCreateValidatorGames matchId={matchId} />
            <Modal
                title="Create validator game"
                visible={visible}
                okText="Create"
                onOk={(): void => { void create(); }}
                onCancel={(): void => { if (!saving) setVisible(false); }}
                okButtonProps={{ disabled: !validEmail, loading: saving }}
                cancelButtonProps={{ disabled: saving }}
                closable={!saving}
                maskClosable={!saving}
                keyboard={!saving}
                footer={createdId ? <Button onClick={(): void => setVisible(false)}>Close</Button> : undefined}
                destroyOnClose
            >
                {createdId ? (
                    <Alert type="success" showIcon message="Validator game created"
                        description={<a href={`/match-log/${createdId}`}>Open validator game</a>} />
                ) : (
                    <>
                        <p>Use match {matchId} as the template for this scorer.</p>
                        <label htmlFor={`validator-email-${matchId}`}>Scorer email</label>
                        <Input id={`validator-email-${matchId}`} type="email" value={email} maxLength={200}
                            disabled={saving} autoComplete="off"
                            onChange={(event): void => setEmail(event.target.value)}
                            onPressEnter={(): void => { void create(); }} />
                        {error && <Alert type="error" showIcon message={error} style={{ marginTop: 12 }} />}
                    </>
                )}
            </Modal>
        </>
    );
}
